import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Sparkles } from 'lucide-react';

/** Marketing copy of a product line, used in follow-up emails to a PoS. */
export interface ProductMarketingForm {
  tagline: string;
  description: string;
  /** One claim per line in the textarea, e.g. "Bio" / "vegan". */
  claims: string;
  nutrition_note: string;
  base_note: string;
  ordering_channels: string;
  lead_priority: '' | 'urgent';
}

export const EMPTY_MARKETING: ProductMarketingForm = {
  tagline: '',
  description: '',
  claims: '',
  nutrition_note: '',
  base_note: '',
  ordering_channels: '',
  lead_priority: '',
};

/** jsonb columns reach the client as an array, but tolerate a raw JSON string too. */
function claimsToLines(claims: unknown): string {
  const value =
    typeof claims === 'string'
      ? (() => {
          try {
            return JSON.parse(claims);
          } catch {
            return [claims];
          }
        })()
      : claims;
  if (!Array.isArray(value)) return '';
  return value.filter((claim): claim is string => typeof claim === 'string').join('\n');
}

export function marketingFormFrom(product: {
  tagline?: string | null;
  description?: string | null;
  claims?: string[] | null;
  nutrition_note?: string | null;
  base_note?: string | null;
  ordering_channels?: string | null;
  lead_priority?: 'urgent' | null;
}): ProductMarketingForm {
  return {
    tagline: product.tagline || '',
    description: product.description || '',
    claims: claimsToLines(product.claims),
    nutrition_note: product.nutrition_note || '',
    base_note: product.base_note || '',
    ordering_channels: product.ordering_channels || '',
    lead_priority: product.lead_priority === 'urgent' ? 'urgent' : '',
  };
}

/** Empty strings are sent as null so the backend clears the column. */
export function marketingPayload(form: ProductMarketingForm) {
  const text = (value: string) => value.trim() || null;
  return {
    tagline: text(form.tagline),
    description: text(form.description),
    claims: form.claims
      .split('\n')
      .map((claim) => claim.trim())
      .filter(Boolean),
    nutrition_note: text(form.nutrition_note),
    base_note: text(form.base_note),
    ordering_channels: text(form.ordering_channels),
    lead_priority: form.lead_priority === 'urgent' ? 'urgent' : null,
  };
}

const INPUT_CLASS =
  'w-full p-3 bg-gray-50 border-2 border-gray-100 rounded-2xl font-medium ' +
  'focus:border-brand-500 transition-colors text-sm';

const LABEL_CLASS =
  'block text-xs font-black text-gray-400 uppercase tracking-widest mb-2';

interface ProductMarketingFieldsProps {
  value: ProductMarketingForm;
  onChange: (value: ProductMarketingForm) => void;
}

/**
 * Collapsed by default: the fields only matter for the follow-up email, so they must not
 * crowd out name and manufacturer in the everyday "add product" flow.
 */
export const ProductMarketingFields: React.FC<ProductMarketingFieldsProps> = ({
  value,
  onChange,
}) => {
  const filledCount = [
    value.tagline,
    value.description,
    value.claims,
    value.nutrition_note,
    value.base_note,
    value.ordering_channels,
  ].filter((entry) => entry.trim()).length;
  const [open, setOpen] = useState(filledCount > 0);

  const set = (patch: Partial<ProductMarketingForm>) =>
    onChange({ ...value, ...patch });

  return (
    <div className="border-2 border-gray-100 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-gray-50 text-left"
      >
        {open ? (
          <ChevronDown size={15} className="text-brand-500" />
        ) : (
          <ChevronRight size={15} className="text-gray-400" />
        )}
        <Sparkles size={14} className="text-brand-500" />
        <span className="text-xs font-black uppercase tracking-widest text-gray-600">
          Email content
        </span>
        <span className="text-xs text-gray-400 ml-auto">
          {filledCount > 0 ? `${filledCount} of 6 filled` : 'empty'}
        </span>
      </button>

      {open && (
        <div className="p-4 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={value.lead_priority === 'urgent'}
              onChange={(e) => set({ lead_priority: e.target.checked ? 'urgent' : '' })}
              className={
                'mt-1 h-4 w-4 rounded border-gray-300 text-fuchsia-700 focus:ring-fuchsia-500'
              }
            />
            <span>
              <span className="block text-sm font-semibold text-gray-900">
                Ernsthaftes Interesse
              </span>
              <span className="block text-xs text-gray-500 mt-0.5">
                Shows a visit button that opens a follow-up above high.
              </span>
            </span>
          </label>
          <p className="text-xs text-gray-500">
            Used in the follow-up email after a visit. Without a tagline or description
            the email warns that copy is missing.
          </p>
          <div>
            <label className={LABEL_CLASS}>Tagline</label>
            <input
              type="text"
              value={value.tagline}
              onChange={(e) => set({ tagline: e.target.value })}
              placeholder="e.g. Not a Soda"
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Description</label>
            <textarea
              value={value.description}
              onChange={(e) => set({ description: e.target.value })}
              rows={3}
              placeholder="Two or three sentences a customer should read about this line."
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Claims (one per line)</label>
            <textarea
              value={value.claims}
              onChange={(e) => set({ claims: e.target.value })}
              rows={3}
              placeholder={'Bio\nvegan\nohne Zuckerzusatz'}
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Nutrition note</label>
            <textarea
              value={value.nutrition_note}
              onChange={(e) => set({ nutrition_note: e.target.value })}
              rows={2}
              placeholder="e.g. Nährwerte je 100 ml: 19 kcal."
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Base note</label>
            <textarea
              value={value.base_note}
              onChange={(e) => set({ base_note: e.target.value })}
              rows={2}
              placeholder="e.g. Basis: Wasser mit 30 % Fruchtsaft."
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Ordering channels</label>
            <textarea
              value={value.ordering_channels}
              onChange={(e) => set({ ordering_channels: e.target.value })}
              rows={2}
              placeholder="e.g. Direkt über BrandCircuit oder via Selgros."
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>
        </div>
      )}
    </div>
  );
};
